import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';

const ALLOWED_EMAILS = (
  process.env.REACT_APP_ALLOWED_EMAILS
    ? process.env.REACT_APP_ALLOWED_EMAILS.split(',').map(e => e.trim().toLowerCase())
    : ['e.claude140@gmail.com', 'oceaneamoro.w@gmail.com']
);

interface AuthContextType {
  currentUser: User | null;
  session: Session | null;
  loading: boolean;
  authError: string | null;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  session: null,
  loading: true,
  authError: null,
  logout: async () => {},
  signInWithGoogle: async () => {},
  clearAuthError: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const handleSession = async (currentSession: Session | null) => {
    if (!currentSession || !currentSession.user) {
      setSession(null);
      setCurrentUser(null);
      setLoading(false);
      return;
    }

    const email = currentSession.user.email?.toLowerCase();
    if (email && !ALLOWED_EMAILS.includes(email)) {
      console.warn(`Compte non autorisé : ${email}`);
      await supabase.auth.signOut();
      setSession(null);
      setCurrentUser(null);
      setAuthError(`Le compte Google (${email}) n'est pas autorisé à accéder à cette application.`);
      setLoading(false);
      return;
    }

    setSession(currentSession);
    setCurrentUser(currentSession.user);
    setAuthError(null);
    setLoading(false);

    // Clean OAuth tokens / codes from address bar
    if (window.location.hash.includes('access_token') || window.location.search.includes('code=')) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session);
    }).catch(err => {
      console.error('Erreur getSession:', err);
      setLoading(false);
    });

    // Listen for auth changes (OAuth callbacks, token refresh, sign-out)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      await handleSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    setAuthError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    });
    if (error) throw error;
  };

  const logout = async () => {
    setAuthError(null);
    await supabase.auth.signOut();
    setSession(null);
    setCurrentUser(null);
  };

  const clearAuthError = () => {
    setAuthError(null);
  };

  const value = {
    currentUser,
    session,
    loading,
    authError,
    logout,
    signInWithGoogle,
    clearAuthError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

